import { createTheme } from '@mui/material/styles';

import { colors } from './colors';
import { gradients } from './gradients';

const inputPadding = '12px';

const optionStyles = {
  padding: '10px 12px',
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: colors.white.hover,
  },
  '&:not(:first-child)': {
    borderTop: `1px solid ${colors.gray.primary}`,
  },
  '&.Mui-selected': {
    backgroundColor: colors.white.primary,
    '&:hover': {
      backgroundColor: colors.white.hover,
    },
  },
};

const theme = createTheme({
  cssVariables: true,
  palette: {
    ...colors,
    gradients,
    text: {
      primary: colors.black.primary,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: 'Montserrat',
    caption: {
      fontSize: '12px',
      lineHeight: '16px',
    },
    h2: {
      fontWeight: 700,
      fontSize: '18px',
      lineHeight: '28px',
    },
    h1: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '32px',
    },
    h3: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '22px',
    },
    body2: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
    },
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: optionStyles,
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0px',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&.MuiMenu-list': {
            padding: 0,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        endIcon: {
          marginRight: 0,
        },
        startIcon: {
          marginLeft: 0,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '.MuiBackdrop-root': {
            background: `${colors.black.primary}CC`,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          width: 4,
          height: 4,
          background: gradients.accentGradient.primary,
        },
      },
    },
    // @ts-ignore
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: gradients.accentGradient.primary,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
          '&.Mui-checked': {
            color: gradients.accentGradient.primary,
          },
        },
      },
    },
    // @ts-ignore
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          '& .MuiYearCalendar-button.Mui-selected': {
            background: gradients.accentGradient.primary,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: '8px',
          padding: 0,
          width: '100%',
          marginLeft: 'unset',
          marginRight: 'unset',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            margin: '0 4px',
            fontSize: '12px',
            lineHeight: '16px',
            color: colors.red.primary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          top: 10,
          right: 12,
        },
        root: {
          background: gradients.accentGradient.primary,
          '&:hover': {
            background: gradients.accentGradient.hover,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: '12px',
          fontSize: '12px',
          lineHeight: '16px',
          borderRadius: '8px',
          color: colors.black.primary,
          background: colors.white.primary,
          border: `1.5px solid ${colors.gray.primary}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          margin: 16,
          boxShadow: 'none',
          borderRadius: '16px',
          maxHeight: 'calc(100% - 32px)',
          background: colors.white.primary,
          [theme.breakpoints.up('md')]: {
            margin: 24,
            maxHeight: 'calc(100% - 48px)',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiMenu-paper': {
            padding: '1px',
            display: 'flex',
            boxShadow: 'none',
            overflow: 'hidden',
            borderRadius: '16px',
            background: gradients.accentGradient.primary,
            '.MuiList-root': {
              width: '100%',
              maxHeight: '100%',
              overflowY: 'auto',
              borderRadius: '15px',
              background: colors.white.primary,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            height: 24,
            borderRadius: '15px',
            padding: inputPadding,
            background: colors.white.primary,

            '&::placeholder': {
              opacity: 1,
              color: colors.gray.primary,
            },
            '&::-moz-placeholder': {
              opacity: 1,
              color: colors.gray.primary,
            },
            '&:-ms-input-placeholder': {
              opacity: 1,
              color: colors.gray.primary,
            },
            '&::-webkit-input-placeholder': {
              opacity: 1,
              color: colors.gray.primary,
            },
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          width: 47,
          height: 47,
          display: 'flex',
          fontWeight: 600,
          alignItems: 'center',
          backgroundClip: 'text',
          justifyContent: 'center',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundImage: gradients.accentGradient.primary,
          '.MuiTouchRipple-root': {
            display: 'none',
          },
          '&:not(.Mui-selected):not(.MuiPaginationItem-ellipsis):hover': {
            backgroundImage: gradients.accentGradient.hover,
          },
          '&:not(.Mui-selected):not(.MuiPaginationItem-ellipsis):active': {
            backgroundImage: gradients.accentGradient.active,
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiInputAdornment-positionEnd': {
            transform: 'rotate(180deg)',
          },
        },
        inputRoot: {
          padding: '1px !important',
          '.MuiInputAdornment-positionEnd': {
            top: 10,
            right: '12px !important',
          },
          '.MuiOutlinedInput-input': {
            borderRadius: 7,
            cursor: 'pointer',
            padding: inputPadding,
            paddingRight: '48px !important',
          },
        },
        paper: {
          padding: '2px',
          boxShadow: 'none',
          borderRadius: '8px',
          background: gradients.accentGradient.primary,
          '.MuiAutocomplete-listbox': {
            padding: 0,
          },
          '.MuiAutocomplete-noOptions': {
            padding: '10px 12px',
          },
          '.MuiAutocomplete-listbox, .MuiAutocomplete-noOptions': {
            width: '100%',
            borderRadius: '7px',
            background: colors.white.primary,
            '.MuiAutocomplete-option': optionStyles,
          },
        },
      },
    },

    // @ts-ignore
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '1px',
          borderRadius: '8px',
          background: colors.gray.primary,

          '&.Mui-error': {
            background: colors.red.primary,
          },

          '& .MuiPickersOutlinedInput-notchedOutline': {
            display: 'none',
          },

          '.MuiPickersOutlinedInput-input': {
            backgroundColor: colors.white.hover,
          },

          '&.Mui-focused, &.Mui-focused:hover': {
            background: gradients.accentGradient.primary,
          },

          '& .MuiPickersSectionList-root': {
            opacity: 'unset',
            borderRadius: '7px',
            padding: '10px 12px',
            background: colors.white.primary,
          },

          '& .MuiPickersOutlinedInput-input': {
            height: '24px',
            borderRadius: '15px',
            padding: inputPadding,
            background: colors.white.primary,
          },

          '&:hover:not(.Mui-focused)': {
            background: colors.gray.primary,
            '& .MuiPickersSectionList-root': {
              background: colors.white.hover,
            },
          },

          '& .MuiInputAdornment-root': {
            margin: 0,
            top: '50%',
            padding: 0,
            right: '14px',
            position: 'absolute',
            transform: 'translateY(-50%)',
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
          '&.MuiSelect-select': {
            borderRadius: '15px',
            paddingRight: '48px !important',
            background: colors.white.primary,

            '&:hover': {
              background: colors.white.hover,
            },
          },
        },
        root: {
          padding: '1px',
          borderRadius: '16px',
          background: colors.border.primary,
          '&:hover': {
            background: colors.border.primary,
          },
          '&.MuiInputBase-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              display: 'none',
            },
            '&:hover .MuiOutlinedInput-input': {
              backgroundColor: colors.white.hover,
            },
            '& input:-webkit-autofill': {
              WebkitTextFillColor: colors.black.primary,
              WebkitBoxShadow: `0 0 0 1000px ${colors.white.primary} inset`,
            },
            '&.Mui-error': {
              background: colors.red.primary,

              '.MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
            },
            '&.Mui-focused': {
              background: gradients.accentGradient.primary,

              '.MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
              '.search-icon': {
                path: {
                  fill: colors.black.primary,
                },
              },
            },
            '&.MuiInputBase-adornedEnd': {
              '.MuiOutlinedInput-input': {
                paddingRight: 38,
              },
              '.MuiInputAdornment-positionEnd': {
                top: 10,
                width: 24,
                right: 10,
                height: 24,
                display: 'flex',
                position: 'absolute',
              },
            },
            '&.MuiInputBase-adornedStart': {
              '.MuiOutlinedInput-input': {
                paddingLeft: 38,
              },
              '.MuiInputAdornment-positionStart': {
                top: 12,
                left: 12,
                width: 24,
                height: 24,
                display: 'flex',
                position: 'absolute',
              },
            },
            '&.Mui-disabled': {
              background: colors.gray.primary,
              '&:hover': {
                background: colors.gray.primary,
              },
              '&:hover .MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
              '.MuiOutlinedInput-input': {
                color: colors.gray.primary,
                backgroundColor: colors.white.primary,
              },
            },
          },
        },
      },
    },
  },
});

export { colors, gradients };
export default theme;
